/**
 * 逻辑流编辑器 - 核心 Hook
 */

import {useCallback, useMemo} from 'react';
import {useLogicFlowStore} from '../store/logicFlowStore';
import type {LogicFlowNodeType, StrategyConfig} from '../types/logicFlow';
import {generateStrategyCode} from '../utils/logicFlowCodegen';
import type {MyStrategyItem} from '../services/myStrategyService';
import {myStrategyService} from '../services/myStrategyService';

export function useLogicFlowEditor() {
    const store = useLogicFlowStore();

    // 生成策略代码（前端快速预览）
    const generatedCode = useMemo(() => {
        const {visualModel} = store;
        return generateStrategyCode(
            visualModel.graph,
            visualModel.config,
            visualModel.strategyClassName
        );
    }, [store.visualModel]);

    // 校验并生成代码（调后端）
    const validate = useCallback(async () => {
        const {visualModel} = store;
        store.setIsSaving(true);
        try {
            const result = await myStrategyService.validateStrategyVisualModel({
                visualModel: JSON.stringify(visualModel),
                strategyName: visualModel.strategyName,
                title: visualModel.title,
                description: visualModel.description,
            });
            store.setValidationResult(result);
            return result;
        } catch (e: any) {
            const errorResult = {
                valid: false,
                errors: [e.message || '校验失败'],
                warnings: [] as string[],
            };
            store.setValidationResult(errorResult);
            return errorResult;
        } finally {
            store.setIsSaving(false);
        }
    }, [store.visualModel]);

    // 保存策略
    const save = useCallback(async () => {
        const {visualModel, strategyId} = store;

        // 先校验
        const validationResult = await validate();
        if (!validationResult.valid) {
            return {success: false, errors: validationResult.errors};
        }

        store.setIsSaving(true);
        try {
            const vResult = validationResult as { normalizedVisualModel?: string; generatedStrategyCode?: string };
            const normalizedModel = vResult.normalizedVisualModel || JSON.stringify(visualModel);
            const generatedCode = vResult.generatedStrategyCode || '';

            const payload = {
                title: visualModel.title || '未命名策略',
                strategyName: visualModel.strategyName,
                description: visualModel.description,
                visualModel: normalizedModel,
                strategyCode: generatedCode,
                isActive: true,
            };

            let result: MyStrategyItem;
            if (strategyId != null) {
                result = await myStrategyService.updateMyStrategy(strategyId, payload);
            } else {
                result = await myStrategyService.createMyStrategy(payload);
            }

            const snapshot = JSON.stringify({
                graph: visualModel.graph,
                config: visualModel.config,
                title: visualModel.title,
            });
            store.markSaved(result.id, snapshot);
            store.setValidationResult(null);
            return {success: true, strategyId: result.id};
        } catch (e: any) {
            return {success: false, errors: [e.message || '保存失败']};
        } finally {
            store.setIsSaving(false);
        }
    }, [store.visualModel, store.strategyId, validate]);

    const addNode = useCallback(
        (type: LogicFlowNodeType) => {
            store.addNode(type);
        },
        [store.addNode]
    );

    const updateConfig = useCallback(
        (partial: Partial<StrategyConfig>) => {
            store.updateConfig(partial);
        },
        [store.updateConfig]
    );

    const updateMeta = useCallback(
        (meta: { strategyName?: string; title?: string; description?: string }) => {
            const {visualModel} = store;
            const newModel = {
                ...visualModel,
                ...meta,
                strategyClassName: meta.strategyName || visualModel.strategyClassName,
            };
            store.loadModel(newModel, store.strategyId);
        },
        [store]
    );

    return {
        ...store,
        generatedCode,
        validate,
        save,
        addNode,
        updateConfig,
        updateMeta,
        autoLayout: store.autoLayout,
    };
}
